import React, {
  useEffect,
  useState,
  FunctionComponent,
  useId,
  useRef
} from 'react';
import { rovingIndex } from 'roving-ux';
import { SearchBox as HeadlessSearchBox } from '@coveo/headless';
import { clearAllFacetsContext } from '../../helpers/coveoFacet';

interface SearchBoxProps {
  controller: HeadlessSearchBox;
  searchLabel: string;
  searchPlaceholder: string;
  submitLabel: string;
  clearLabel: string;
  maxSuggestions?: number;
  onSearchSubmit?: (value: string, type?: string) => void;
}

export const SEARCH_BOX_EVENTS = Object.freeze({
  updateDidYouMean: 'searchbox:updateDidYouMean'
});

export const SearchBox: FunctionComponent<SearchBoxProps> = props => {
  const id = useId();
  const {
    controller,
    searchLabel,
    searchPlaceholder,
    submitLabel,
    clearLabel,
    maxSuggestions,
    onSearchSubmit
  } = props;
  const [state, setState] = useState(controller.state);
  const [value, setValue] = useState(controller.state.value);
  const [focus, setFocus] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const isEnterKey = (e: React.KeyboardEvent<HTMLDivElement>) =>
    e.key === 'Enter';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (input.current === document.activeElement) {
      if (isEnterKey(e)) {
        e.preventDefault();
        clearAllFacetsContext();
        onSearchSubmit && onSearchSubmit(value, 'search box');
        controller.updateText(value);
        setFocus(false);
        controller.submit();
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (listRef.current) {
          if (e.key === 'ArrowDown') {
            const firstElement = listRef.current.querySelector('li');
            if (firstElement) {
              firstElement.querySelector('button')?.focus();
              setFocus(true);
            }
          } else {
            const lastElement = listRef.current.querySelector('li:last-child');
            if (lastElement) {
              (lastElement as HTMLLIElement).querySelector('button')?.focus();
              setFocus(true);
            }
          }
        }
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        setFocus(false);
        input.current?.blur();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      input.current?.focus();
      setFocus(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 2) {
      controller.updateText(e.target.value);
      setFocus(true);
    }

    if (e.target.value.length === 0) {
      handleClear();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    if (e.target.value.length > 2) {
      controller.updateText(e.target.value);
    }
  };

  const handleSuggestionClick = (value: string) => {
    onSearchSubmit && onSearchSubmit(value, 'suggestion box');
    clearAllFacetsContext();
    setValue(value);
    controller.selectSuggestion(value);
  };

  const handleMouseDown = (e: Event) => {
    if ((e.target as HTMLElement)?.closest('.searchbox__input-wrapper')) {
      return;
    } else {
      setFocus(false);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLElement>) => {
    if (
      !(e.relatedTarget as HTMLElement)?.closest('.searchbox__input-wrapper')
    ) {
      setFocus(false);
    }
  };

  const handleDidYouMean = (e: CustomEventInit) => {
    const query = e.detail.query;
    setValue(query);
    clearAllFacetsContext();
    onSearchSubmit && onSearchSubmit(query);
  };

  const handleClear = (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e?.preventDefault();
    setValue('');
    clearAllFacetsContext();
    onSearchSubmit && onSearchSubmit('');
    controller.updateText('');
    controller.submit();
  };

  const handleSearchSubmit = (value: string) => {
    clearAllFacetsContext();
    onSearchSubmit && onSearchSubmit(value, 'search box');
    controller.updateText(value);
    setFocus(false);
    controller.submit();
  };

  useEffect(() => {
    controller.subscribe(() => setState(controller.state));
    window.addEventListener(
      SEARCH_BOX_EVENTS.updateDidYouMean,
      handleDidYouMean
    );
    window.addEventListener('mousedown', e => handleMouseDown(e));

    return () => {
      window.removeEventListener(
        SEARCH_BOX_EVENTS.updateDidYouMean,
        handleDidYouMean
      );

      window.removeEventListener('mousedown', e => handleMouseDown(e));
    };
  }, []);

  useEffect(() => {
    if (!value) {
      setValue(state.value);
      onSearchSubmit && onSearchSubmit(state.value);
    }
  }, [state.value]);

  useEffect(() => {
    if (listRef.current) {
      rovingIndex({
        element: listRef.current,
        target: '.predictive-search__item'
      });
    }
  }, [state.suggestions]);

  return (
    <div
      className="searchbox"
      onKeyDown={e => {
        handleKeyDown(e);
      }}
    >
      <div className="field ">
        <label className="field__label " htmlFor={`searchbox-${id}`}>
          {searchLabel}
        </label>
        <div className="searchbox__input-wrapper">
          <input
            className="field__input"
            id={`searchbox-${id}`}
            name={`searchbox-${id}`}
            placeholder={searchPlaceholder}
            value={value}
            onChange={handleChange}
            onInput={handleInputChange}
            onFocus={() => setFocus(true)}
            ref={input}
          />
          {state.suggestions.length > 0 && value.length > 2 && focus && (
            <ul
              className="predictive-search__list"
              ref={listRef}
              onFocus={() => setFocus(true)}
              onBlur={handleInputBlur}
            >
              {state.suggestions.map((suggestion, index) => {
                const value = suggestion.rawValue;
                if (maxSuggestions && index >= maxSuggestions - 1) {
                  return null;
                }
                return (
                  <li key={value}>
                    <button
                      className="predictive-search__item"
                      title={suggestion.rawValue}
                      onClick={() => handleSuggestionClick(value)}
                      dangerouslySetInnerHTML={{
                        __html: suggestion.highlightedValue
                      }}
                      onFocus={() => {
                        setFocus(true);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {value.length > 0 && (
          <button
            className="field__input-close"
            title={clearLabel}
            onClick={handleClear}
          >
            <span className="sr-only">{clearLabel}</span>
          </button>
        )}
        <button
          className="field__input-search"
          title={searchLabel}
          onClick={() => handleSearchSubmit(value)}
          disabled={state.value.length === 0}
          onBlur={e => handleInputBlur(e)}
        >
          <span className="sr-only">{submitLabel}</span>
        </button>
      </div>
    </div>
  );
};
